import { useMemo } from "react";
import { TabsList, TabsTrigger } from "@/components/molecules/tabs";
import { Separator } from "@/components/atoms/separator";
import { cn } from "@/lib/utils";
import { EDITOR_EDITED_HIGHLIGHT_CLASS } from "@/features/sparxsEditor/components/atoms/editorConstants";
import { useActiveData } from "@/features/sparxsEditor/hooks/activeData/useActiveData";
import { useActiveFileName } from "@/features/sparxsEditor/hooks/activeData/useActiveFileName";
import { useOriginalData } from "@/features/sparxsEditor/hooks/activeData/useOriginalData";
import { useHasSparxTeaser } from "@/features/sparxsEditor/hooks/activeData/useSparxTeaser";
import { getEditedTabs } from "@/features/sparxsEditor/lib/form/formEditedPaths";
import { getAvailableSparxTabs } from "@/features/sparxsEditor/lib/editor/sparxTabAvailability";

export enum SparxTab {
  TEASER = "Teaser",
  METADATA = "Metadata",
  CONTENT = "Content",
  ADVISOR = "Advisor",
}

const a11yProps = (index: string) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export type EditorTabsSelectorProps = {
  displayTab: SparxTab;
};

export const EditorTabsSelector = ({ displayTab }: EditorTabsSelectorProps) => {
  const activeSparxData = useActiveData();
  const activeFileName = useActiveFileName();
  const sparxOriginalData = useOriginalData();
  const hasTeaser = useHasSparxTeaser();

  const editedTabs = useMemo(() => {
    if (activeSparxData == null || sparxOriginalData == null)
      return {
        content: false,
        advisor: false,
        metadata: false,
      };
    return getEditedTabs(activeSparxData, sparxOriginalData);
  }, [activeSparxData, sparxOriginalData]);

  const available = useMemo(
    () => getAvailableSparxTabs(activeSparxData, { hasTeaser }),
    [activeSparxData, hasTeaser],
  );

  // Include active file so sparx switches snap the underline even when the
  // visible tab set is unchanged and only `value` remaps to a fallback.
  const tabsLayoutKey = useMemo(
    () =>
      [
        activeFileName,
        ...[
          SparxTab.TEASER,
          SparxTab.CONTENT,
          SparxTab.ADVISOR,
          SparxTab.METADATA,
        ].filter((tab) => available.has(tab)),
      ].join("\0"),
    [activeFileName, available],
  );

  return (
    <div
      className="relative shrink-0 overflow-hidden pb-px"
      data-editor-tabs=""
    >
      <TabsList
        variant="line"
        value={displayTab}
        layoutKey={tabsLayoutKey}
        className="w-full"
      >
        {available.has(SparxTab.TEASER) && (
          <TabsTrigger
            variant="line"
            value={SparxTab.TEASER}
            className="text-(length:--console-type-nav) font-medium"
            {...a11yProps(SparxTab.TEASER)}
          >
            {SparxTab.TEASER}
          </TabsTrigger>
        )}
        {available.has(SparxTab.CONTENT) && (
          <TabsTrigger
            variant="line"
            value={SparxTab.CONTENT}
            className={cn(
              "text-(length:--console-type-nav) font-medium",
              editedTabs.content && EDITOR_EDITED_HIGHLIGHT_CLASS,
            )}
            data-edited={editedTabs.content ? "true" : undefined}
            {...a11yProps(SparxTab.CONTENT)}
          >
            {SparxTab.CONTENT}
          </TabsTrigger>
        )}
        {available.has(SparxTab.ADVISOR) && (
          <TabsTrigger
            variant="line"
            value={SparxTab.ADVISOR}
            className={cn(
              "text-(length:--console-type-nav) font-medium",
              editedTabs.advisor && EDITOR_EDITED_HIGHLIGHT_CLASS,
            )}
            data-edited={editedTabs.advisor ? "true" : undefined}
            {...a11yProps(SparxTab.ADVISOR)}
          >
            {SparxTab.ADVISOR}
          </TabsTrigger>
        )}
        {available.has(SparxTab.METADATA) && (
          <TabsTrigger
            variant="line"
            value={SparxTab.METADATA}
            className={cn(
              "text-(length:--console-type-nav) font-medium",
              editedTabs.metadata && EDITOR_EDITED_HIGHLIGHT_CLASS,
            )}
            data-edited={editedTabs.metadata ? "true" : undefined}
            {...a11yProps(SparxTab.METADATA)}
          >
            {SparxTab.METADATA}
          </TabsTrigger>
        )}
      </TabsList>
      <Separator
        className="
          absolute inset-x-0 bottom-0 z-10 my-0 bg-(--console-border-subtle)
        "
      />
    </div>
  );
};
